import type { Component, MaybeRefOrGetter, Ref, WritableComputedRef } from "vue";
import { computed, getCurrentInstance, toValue } from "vue";

export type ValidationStatus = "warning" | "error" | "success";

export type Size = "sm" | "md" | "lg" | "xs";

export type OptionValue = string | number | null;
export type OptionLabel = string | (() => Component);

export type OptionSelect = {
  label: OptionLabel;
  value: OptionValue;
  btnLabel?: OptionLabel;
  disabled?: boolean;
  textValue?: string;
  separator?: boolean;
};

// A bare "separator" entry renders a native divider between items (same
// convention as the data-table actions menu), everything else is an option.
export type GlobalOptionsSelect = OptionSelect | "separator" | (string & {});

export function toOptionSelect(option: OptionSelect | string): OptionSelect {
  if (typeof option === "string") {
    return {
      label: option,
      value: option,
    };
  }

  return option;
}

export function isOptionValue(option: OptionSelect | OptionValue): option is OptionValue {
  return typeof option === "string" || typeof option === "number" || option === null;
}

const CAST_NULL_PREFIX = "__cast:null__";
const CAST_NUMBER_PREFIX = "__cast:number__";

// Options are keyed by string in the DOM, so null/number values are round-tripped
// through these prefixes. Shared by useStringifiedOptions and normalizeOptions.
function stringifyOptionValue(value: OptionValue): string {
  if (value === null) return CAST_NULL_PREFIX;
  if (typeof value === "number") return `${CAST_NUMBER_PREFIX}${value}`;
  return value;
}

function parseOptionValue(value: string): OptionValue {
  if (value === CAST_NULL_PREFIX) return null;
  if (value.startsWith(CAST_NUMBER_PREFIX)) return Number.parseInt(value.slice(CAST_NUMBER_PREFIX.length));
  return value;
}

export function useNumericOptions(
  options: Ref<(Omit<OptionSelect, "value"> & { value: number | null })[]>,
  nullableValue: string = "___"
): WritableComputedRef<OptionSelect[]> {
  return computed<OptionSelect[]>({
    get() {
      return options.value.map((option) => {
        return {
          ...option,
          value: option.value === null ? nullableValue : (option.value as number).toString(),
        };
      });
    },
    set(val) {
      options.value = val.map((option) => {
        return {
          ...option,
          value: option.value === nullableValue ? null : Number.parseInt(option.value as string),
        };
      });
    },
  });
}

type StrigyfiedOption = Omit<OptionSelect, "value"> & { value: string };

export function useStringifiedOptions<P extends object, K extends keyof P>(
  props: P,
  key: K,
  options: MaybeRefOrGetter<GlobalOptionsSelect[]>
) {
  const stringifiedOptions = computed<StrigyfiedOption[]>(() => {
    return toValue(options).map((option) => {
      if (option === "separator") {
        return { separator: true, label: "", value: "__separator__" };
      }

      if (typeof option === "string") {
        return {
          label: option,
          value: option,
        };
      }

      return {
        ...option,
        value: stringifyOptionValue(option.value),
      };
    });
  });

  const vm = getCurrentInstance();

  const emit =
    // @ts-expect-error
    vm?.emit || vm?.$emit?.bind(vm) || vm?.proxy?.$emit?.bind(vm?.proxy);

  const vModel = computed({
    get() {
      return stringifyOptionValue(props[key] as OptionValue);
    },
    set(val) {
      emit(`update:${String(key)}`, parseOptionValue(val));
    },
  });

  return {
    options: stringifiedOptions,
    vModel,
  };
}

export function normalizeOptions(
  options: WritableComputedRef<OptionSelect[]>
): WritableComputedRef<(Omit<OptionSelect, "value"> & { value: string })[]> {
  return computed({
    get() {
      return options.value.map((option) => {
        return {
          ...option,
          value: stringifyOptionValue(option.value),
        };
      });
    },
    set(val) {
      options.value = val.map((option) => {
        return {
          ...option,
          value: parseOptionValue(option.value),
        };
      });
    },
  });
}
